import React from 'react';
import { Link } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';
import * as Motion from 'motion/react';
import ConnectionState from '../ConnectionState';
import DeviceImage from '../DeviceImage';
import { SessionListSessionTypePill } from '../Pills';
import { UserAvatar } from '../UserProfileItem';
import { formatDate, getDevicePrettyName } from '../../util';
import {
  animationStaggeredDashboardListContainer,
  animationStaggeredDashboardListItem,
} from '../../animations';
import './styles.scss';

const { motion } = Motion;


type ListItem = Record<string, any>;

type RenderComponentProps = {
  item: ListItem;
};

const DashboardList = (
  {
    items,
    RenderComponent
  }: {
    items: ListItem[],
    RenderComponent: React.ComponentType<RenderComponentProps>
  }) => {

  if (!items?.length) {
    return null;
  }

  return (
    <motion.div
      variants={animationStaggeredDashboardListContainer}
      initial="hidden"
      animate="visible"
      className="dashboard-list-wrapper">
      {
        items?.map((item: ListItem, index: number) => (
          <RenderComponent key={index} item={item}/>
        ))
      }
    </motion.div>
  )
};


type DashboardUserListRenderComponentProps = {
  item: ListItem
}

export const DashboardUserListRenderComponent = ({ item }: DashboardUserListRenderComponentProps) => {
  return (
    <motion.div variants={animationStaggeredDashboardListItem} className="dashboard-list-item">
      <Link className="dashboard-list-item-link" to={`/users/${item.id}`}>
        <div className="dashboard-list-item-row">
          <UserAvatar userName={item?.userName}/>

          <div className="dashboard-list-item-title-wrapper">
            <p>{item?.userName}</p>
            <span className={'dashboard-list-item-id'}>{item?.id}</span>
          </div>
        </div>

        <span className="dashboard-list-item-icon">
          <FaChevronRight/>
        </span>
      </Link>

    </motion.div>
  );
};


type DashboardDeviceListRenderComponentProps = {
  item: ListItem
}

export const DashboardDeviceListRenderComponent = ({ item }: DashboardDeviceListRenderComponentProps) => {
  return (
    <motion.div variants={animationStaggeredDashboardListItem} className="dashboard-list-item">
      <Link className="dashboard-list-item-link" to={`/devices/${item?.deviceId}`}>
        <div className="device-connected-wrapper">
          <ConnectionState isConnected={item?.isConnected}/>
          <DeviceImage deviceType={item?.deviceType}/>

          <div className="dashboard-list-item-title-wrapper">
            {
              item?.User?.userName ?
                <p>{item?.User?.userName}'s {getDevicePrettyName(item?.deviceType)}</p> :
                <p>{getDevicePrettyName(item?.deviceType)} - <small><i>unregistered</i></small></p>
            }

            <span className={'dashboard-list-item-id'}>{item?.deviceId}</span>
          </div>
        </div>

        <span className="dashboard-list-item-icon">
          <FaChevronRight/>
        </span>
      </Link>

    </motion.div>
  );
};


type DashboardProtocolListRenderComponentProps = {
  item: ListItem
}

export const DashboardProtocolListRenderComponent = ({ item }: DashboardProtocolListRenderComponentProps) => {
  return (
    <motion.div variants={animationStaggeredDashboardListItem} className="dashboard-list-item">
      <Link className="dashboard-list-item-link" to={`/protocols/${item.id}`}>
        <div className="dashboard-list-item-row">
          <div className="dashboard-list-item-title-wrapper">
            <p>{item?.name ? item?.name : 'Protocol'}</p>
            <span className={'dashboard-list-item-id'}>{item?.id}</span>
          </div>
        </div>

        <span className="dashboard-list-item-icon">
          <FaChevronRight/>
        </span>
      </Link>

    </motion.div>
  );
};


type DashboardSessionListRenderComponentProps = {
  item: ListItem
}

export const DashboardSessionListRenderComponent = ({ item }: DashboardSessionListRenderComponentProps) => {
  return (
    <motion.div variants={animationStaggeredDashboardListItem} className="dashboard-list-item">
      <Link className="dashboard-list-item-link" to={`/sessions/${item.id}`}>
        <div className="dashboard-list-item-row">
          <div className="dashboard-list-item-title-wrapper">
            <p>{item?.User?.userName}</p>
            <span className={'dashboard-list-item-id'}>{formatDate(item?.timestamp, null, true)}</span>
          </div>
        </div>

        <span className="dashboard-list-item-icon">
          <SessionListSessionTypePill type={item.type}>{item.type}</SessionListSessionTypePill>
          <FaChevronRight/>
        </span>
      </Link>

    </motion.div>
  );
};


type DashboardLeaderboardListRenderComponentProps = {
  item: ListItem
}

export const DashboardLeaderboardListRenderComponent = ({ item }: DashboardLeaderboardListRenderComponentProps) => {
  return (
    <motion.div variants={animationStaggeredDashboardListItem} className="dashboard-list-item">
      <Link className="dashboard-list-item-link" to={`/leaderboards/${item.id}`}>
        <div className="dashboard-list-item-row">
          <div className="dashboard-list-item-title-wrapper">
            <p>{item?.displayName}</p>
            <span className={'dashboard-list-item-id'}>{item?.leaderboardSlug}</span>
          </div>
        </div>

        <span className="dashboard-list-item-icon">
          <FaChevronRight/>
        </span>
      </Link>

    </motion.div>
  );
};


type DashboardLeagueListRenderComponentProps = {
  item: ListItem
}

export const DashboardLeagueListRenderComponent = ({ item }: DashboardLeagueListRenderComponentProps) => {
  return (
    <motion.div variants={animationStaggeredDashboardListItem} className="dashboard-list-item">
      <Link className="dashboard-list-item-link" to={`/leagues/${item.id}`}>
        <div className="dashboard-list-item-row">
          <div className="dashboard-list-item-title-wrapper">
            <p>{item?.displayName}</p>
            <span className={'dashboard-list-item-id'}>{item?.leagueSlug}</span>
          </div>
        </div>

        <span className="dashboard-list-item-icon">
          <FaChevronRight/>
        </span>
      </Link>

    </motion.div>
  );
};


type DashboardUserDeviceListRenderComponentProps = {
  item: ListItem
}

export const DashboardUserDeviceListRenderComponent = ({ item }: DashboardUserDeviceListRenderComponentProps) => {
  return (
    <motion.div variants={animationStaggeredDashboardListItem} className="dashboard-list-item user-device-list-item">
      <Link className="dashboard-list-item-link" to={`/devices/${item?.deviceId}`}>
        <div className="device-connected-wrapper">
          <ConnectionState isConnected={item?.isConnected}/>
          <DeviceImage deviceType={item?.deviceType}/>

          <div className="dashboard-list-item-title-wrapper">
            <p>{getDevicePrettyName(item?.deviceType)}</p>

            <span className={'dashboard-list-item-id'}>{item?.deviceId}</span>
          </div>
        </div>

        <span className="dashboard-list-item-icon">
          <FaChevronRight/>
        </span>
      </Link>

    </motion.div>
  );
};

export default DashboardList;
